import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import { i18n } from '@/lib/i18n'
import type { BadgeProps } from '@/components/ui/badge'
import { isKbBusy, type KbSourceMeta } from '@/lib/kb-status'

interface Envelope<T> {
  success: boolean
  data: T
}

// ── Nhãn tiếng Việt & hằng số ────────────────────────────────────────
// type: faq | description | policy | price | commitment | fact
export const KB_TYPES = ['faq', 'description', 'policy', 'price', 'commitment', 'fact'] as const
export type KbType = (typeof KB_TYPES)[number]

export const KB_TYPE_LABELS: Record<string, string> = {
  get faq() { return i18n.t('aiKnow:hook.type.faq') },
  get description() { return i18n.t('aiKnow:hook.type.description') },
  get policy() { return i18n.t('aiKnow:hook.type.policy') },
  get price() { return i18n.t('aiKnow:hook.type.price') },
  get commitment() { return i18n.t('aiKnow:hook.type.commitment') },
  get fact() { return i18n.t('aiKnow:hook.type.fact') },
}

// format: qa | article
export const KB_FORMATS = ['article', 'qa'] as const
export type KbFormat = (typeof KB_FORMATS)[number]

export const KB_FORMAT_LABELS: Record<string, string> = {
  get article() { return i18n.t('aiKnow:hook.format.article') },
  get qa() { return i18n.t('aiKnow:hook.format.qa') },
}

// risk: low | sensitive
export const KB_RISKS = ['low', 'sensitive'] as const
export type KbRisk = (typeof KB_RISKS)[number]

export const KB_RISK_LABELS: Record<string, string> = {
  get low() { return i18n.t('aiKnow:hook.risk.low') },
  get sensitive() { return i18n.t('aiKnow:hook.risk.sensitive') },
}

// status: active | pending | rejected | archived
export const KB_STATUS_LABELS: Record<string, string> = {
  get active() { return i18n.t('aiKnow:hook.status.active') },
  get pending() { return i18n.t('aiKnow:hook.status.pending') },
  get rejected() { return i18n.t('aiKnow:hook.status.rejected') },
  get archived() { return i18n.t('aiKnow:hook.status.archived') },
}

export function kbStatusVariant(status?: string | null): BadgeProps['variant'] {
  switch (status) {
    case 'active':
      return 'success'
    case 'pending':
      return 'warning'
    case 'rejected':
      return 'destructive'
    case 'archived':
      return 'outline'
    default:
      return 'secondary'
  }
}

// Loại cây danh mục: mỗi kho có BA cây riêng — article (thư mục Tài liệu) | qa (danh mục Hỏi đáp) | material (nhóm Ảnh và video).
// Mục chỉ gắn được danh mục CÙNG loại (backend chặn).
export const KB_CATEGORY_KINDS = ['article', 'qa', 'material'] as const
export type KbCategoryKind = (typeof KB_CATEGORY_KINDS)[number]

export const KB_CATEGORY_KIND_LABELS: Record<KbCategoryKind, string> = {
  get article() { return i18n.t('aiKnow:hook.categoryKind.article') },
  get qa() { return i18n.t('aiKnow:hook.categoryKind.qa') },
  get material() { return i18n.t('aiKnow:hook.categoryKind.material') },
}

/** Cây danh mục hợp lệ cho một mục theo định dạng (qa → Hỏi đáp, còn lại → Tài liệu). */
export function categoryKindForFormat(format: string | null | undefined): KbCategoryKind {
  return format === 'qa' ? 'qa' : 'article'
}

/** Nhãn hiển thị cho entry: FAQ dùng title, bài viết lấy dòng đầu content. */
export function kbLabel(entry: Pick<KnowledgeEntry, 'title' | 'content'>): string {
  const t = entry.title?.trim()
  if (t) return t
  const firstLine = entry.content.trim().split('\n')[0]
  return firstLine.length > 80 ? firstLine.slice(0, 80) + '…' : firstLine || i18n.t('aiKnow:hook.noContent')
}

// ── Kiểu dữ liệu ─────────────────────────────────────────────────────
export interface KnowledgeEntry {
  id: string
  orgId: string
  type: string
  format: string
  /** Kho tri thức chứa mục (mỗi mục thuộc đúng một kho). */
  knowledgeBaseId: string
  categoryId: string | null
  productId: string | null
  title: string | null
  content: string
  keywords: string | null
  /** Hỏi đáp: câu hỏi tương tự (câu chuẩn = title). */
  questions?: string[] | null
  status: string
  risk: string
  source: string
  confidence: number | null
  version: number
  createdAt: string
  updatedAt: string
  /** Trạng thái nạp / nhúng (Tài liệu cho AI) — xem lib/kb-status.ts. */
  embedStatus?: string | null
  embedError?: string | null
  chunkCount?: number | null
  embeddedAt?: string | null
  sourceMeta?: KbSourceMeta | null
  /** Danh sách ?preview=1: nội dung chỉ là đoạn đầu — mở sửa phải tải bản đầy đủ. */
  contentLength?: number
  contentTruncated?: boolean
  /** Đã sửa từng đoạn (đoạn là nguồn chuẩn; "Sửa toàn văn" sẽ chia lại từ đầu). */
  chunksEdited?: boolean
}

/** Trạng thái vector của một đoạn: ready = khớp nội dung; stale = vector cũ (chờ xử lý lại); pending = chưa có. */
export type KbChunkState = 'ready' | 'stale' | 'pending'

export interface KnowledgeChunk {
  /** null = đoạn tạm (tài liệu chưa chia đoạn lần nào) — chưa sửa được. */
  id: string | null
  index: number
  heading: string | null
  content: string
  tokenEstimate: number
  state: KbChunkState
  updatedAt: string | null
}

export interface KnowledgeChunksView {
  entry: Pick<KnowledgeEntry, 'id' | 'knowledgeBaseId' | 'categoryId' | 'title' | 'format' | 'status' | 'embedStatus' | 'embedError' | 'sourceMeta' | 'updatedAt' | 'embeddedAt'> & {
    chunkCount: number; chunksEdited: boolean; contentLength: number
  }
  chunks: KnowledgeChunk[]
  virtual: boolean
}

export interface KnowledgeCategory {
  id: string
  /** Danh mục chỉ tồn tại bên trong một kho. */
  knowledgeBaseId: string
  parentId: string | null
  name: string
  slug: string
  description: string | null
  kind: string
  sortOrder: number
  entryCount: number
  /** Số bài viết (Tài liệu) / Hỏi đáp trong kho. */
  articleCount?: number
  qaCount?: number
  materialCount?: number
  createdAt: string
  updatedAt: string
}

export interface KnowledgeEntryInput {
  type: string
  title?: string | null
  content: string
  risk: string
  format?: string
  categoryId?: string | null
  /** Kho nhận mục (không gửi → kho của danh mục / "Kho mặc định"). */
  knowledgeBaseId?: string | null
  productId?: string | null
  keywords?: string | null
  /** Hỏi đáp: câu hỏi tương tự (mỗi câu được AI học như một cách hỏi khác). */
  questions?: string[]
  confidence?: number
  source?: string
}

export interface KnowledgeCategoryInput {
  name: string
  knowledgeBaseId?: string | null
  parentId?: string | null
  description?: string | null
  /** Bắt buộc với danh mục cấp 1 (có cha → theo loại của cha). */
  kind?: KbCategoryKind
  sortOrder?: number
}

// ── Knowledge entries query/mutation ─────────────────────────────────
export interface KnowledgeQueryParams {
  /** Mục của một kho. */
  knowledgeBaseId?: string
  status?: string
  categoryId?: string
  productId?: string
  type?: string
  format?: string
  /** '1' = nội dung rút gọn (danh sách tài liệu dài). */
  preview?: '1'
}

export function useKnowledgeEntries(params: KnowledgeQueryParams = {}, opts: { pollWhileBusy?: boolean } = {}) {
  return useQuery<KnowledgeEntry[]>({
    queryKey: ['knowledge', params],
    queryFn: async () => {
      const { data } = await api.get<Envelope<KnowledgeEntry[]>>('/knowledge', { params })
      return data.data
    },
    placeholderData: (prev) => prev,
    // Có mục đang đọc tài liệu / nhúng → làm mới mỗi 3 giây tới khi xong.
    refetchInterval: opts.pollWhileBusy
      ? (q) => ((q.state.data ?? []).some((e) => isKbBusy(e.embedStatus)) ? 3_000 : false)
      : undefined,
  })
}

/** Một mục đầy đủ (nội dung không rút gọn). */
export async function fetchKnowledgeEntry(id: string): Promise<KnowledgeEntry> {
  const { data } = await api.get<Envelope<KnowledgeEntry>>(`/knowledge/${id}`)
  return data.data
}

/** Theo dõi một mục tới khi xong (đọc tài liệu + nhúng) — cho hộp "Thêm kiến thức". */
export function useKnowledgeEntryStatus(id: string | null | undefined) {
  return useQuery<KnowledgeEntry>({
    queryKey: ['knowledge', 'entry', id],
    enabled: !!id,
    queryFn: () => fetchKnowledgeEntry(id!),
    refetchInterval: (q) => (q.state.data && !isKbBusy(q.state.data.embedStatus) ? false : 2_000),
  })
}

// ── Nạp tài liệu (file / link) ───────────────────────────────────────

export async function uploadKnowledgeFile(
  file: File,
  opts: { categoryId?: string | null; knowledgeBaseId?: string | null; onProgress?: (pct: number) => void } = {},
): Promise<KnowledgeEntry> {
  const form = new FormData()
  // Kho / danh mục TRƯỚC file: máy chủ đọc field theo thứ tự trong luồng multipart.
  if (opts.knowledgeBaseId) form.append('knowledgeBaseId', opts.knowledgeBaseId)
  if (opts.categoryId) form.append('categoryId', opts.categoryId)
  form.append('file', file)
  const { data } = await api.post<Envelope<KnowledgeEntry>>('/knowledge/ingest/file', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (e) => {
      if (opts.onProgress && e.total) opts.onProgress(Math.round((e.loaded / e.total) * 100))
    },
  })
  return data.data
}

export async function ingestKnowledgeUrl(url: string, categoryId?: string | null, knowledgeBaseId?: string | null): Promise<KnowledgeEntry> {
  const { data } = await api.post<Envelope<KnowledgeEntry>>('/knowledge/ingest/url', {
    url, categoryId: categoryId || null, knowledgeBaseId: knowledgeBaseId || null,
  })
  return data.data
}

export function useRetryKnowledgeEntry() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<Envelope<{ id: string; embedStatus: string }>>(`/knowledge/${id}/retry`)
      return data.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['knowledge'] }),
  })
}

/** "Nhúng lại tất cả" — chạy nền, trạng thái từng mục cập nhật dần. */
export function useBackfillKnowledge() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (opts: boolean | { force?: boolean; knowledgeBaseId?: string | null } = false) => {
      const body = typeof opts === 'boolean' ? { force: opts } : { force: !!opts.force, knowledgeBaseId: opts.knowledgeBaseId || null }
      const { data } = await api.post<Envelope<{ queued: number; failed: number }>>('/knowledge/embeddings/backfill', body)
      return data.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['knowledge'] }),
  })
}

export function useCreateKnowledgeEntry() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: KnowledgeEntryInput) => {
      const { data } = await api.post<Envelope<KnowledgeEntry>>('/knowledge', input)
      return data.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['knowledge'] }),
  })
}

export function useUpdateKnowledgeEntry() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { id: string; data: Partial<KnowledgeEntryInput> & { changeNote?: string } }) => {
      const { data } = await api.put<Envelope<KnowledgeEntry>>(`/knowledge/${vars.id}`, vars.data)
      return data.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['knowledge'] }),
  })
}

export function useDeleteKnowledgeEntry() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete<Envelope<{ deleted: boolean }>>(`/knowledge/${id}`)
      return data.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['knowledge'] }),
  })
}

export function useApproveKnowledgeEntry() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<Envelope<KnowledgeEntry>>(`/knowledge/${id}/approve`)
      return data.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['knowledge'] }),
  })
}

export function useRejectKnowledgeEntry() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<Envelope<KnowledgeEntry>>(`/knowledge/${id}/reject`)
      return data.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['knowledge'] }),
  })
}

/** Thao tác hàng loạt trên các mục đã chọn (bảng của kho). */
export function useBulkKnowledge() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { ids: string[]; action: 'delete' | 'enable' | 'disable' | 'move'; categoryId?: string | null }) => {
      const { data } = await api.post<Envelope<{ done: number }>>('/knowledge/bulk', input)
      return data.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['knowledge'] }) // gồm cả ['knowledge', 'bases'] (số liệu kho)
      qc.invalidateQueries({ queryKey: ['knowledge-categories'] })
    },
  })
}

// ── Knowledge categories query/mutation ──────────────────────────────
/**
 * Danh mục của MỘT kho (truyền knowledgeBaseId), một cây khi có `kind`; không truyền = mọi danh mục của tổ chức.
 * queryKey ['knowledge-categories', kho, loại] — invalidate theo tiền tố ['knowledge-categories'].
 */
export function useKnowledgeCategories(
  knowledgeBaseId?: string | null,
  opts: { enabled?: boolean; kind?: KbCategoryKind } = {},
) {
  return useQuery<KnowledgeCategory[]>({
    queryKey: ['knowledge-categories', knowledgeBaseId ?? 'all', opts.kind ?? 'all'],
    enabled: opts.enabled ?? true,
    queryFn: async () => {
      const { data } = await api.get<Envelope<KnowledgeCategory[]>>('/knowledge-categories', {
        params: { ...(knowledgeBaseId ? { knowledgeBaseId } : {}), ...(opts.kind ? { kind: opts.kind } : {}) },
      })
      return data.data
    },
  })
}

/** Kéo thả sắp xếp danh mục trong kho. */
export async function reorderKnowledgeCategories(knowledgeBaseId: string, ids: string[]) {
  const { data } = await api.post<Envelope<{ updated: number }>>('/knowledge-categories/reorder', { knowledgeBaseId, ids })
  return data.data
}

export function useCreateKnowledgeCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: KnowledgeCategoryInput) => {
      const { data } = await api.post<Envelope<KnowledgeCategory>>('/knowledge-categories', input)
      return data.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['knowledge-categories'] }),
  })
}

export function useUpdateKnowledgeCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { id: string; data: Partial<KnowledgeCategoryInput> }) => {
      const { data } = await api.patch<Envelope<KnowledgeCategory>>(
        `/knowledge-categories/${vars.id}`,
        vars.data,
      )
      return data.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['knowledge-categories'] }),
  })
}

export function useDeleteKnowledgeCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete<Envelope<{ deleted: boolean }>>(`/knowledge-categories/${id}`)
      return data.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['knowledge-categories'] })
      qc.invalidateQueries({ queryKey: ['knowledge'] })
    },
  })
}

// ── Đoạn của tài liệu (trình đọc đoạn ở tab Tài liệu) ─────────────────
export function useKnowledgeChunks(entryId: string | null | undefined) {
  return useQuery<KnowledgeChunksView>({
    queryKey: ['knowledge', 'chunks', entryId],
    enabled: !!entryId,
    queryFn: async () => {
      const { data } = await api.get<Envelope<KnowledgeChunksView>>(`/knowledge/${entryId}/chunks`)
      return data.data
    },
    placeholderData: (prev) => (prev?.entry.id === entryId ? prev : undefined),
    // Đoạn đang chờ nhúng lại → làm mới mỗi 3 giây tới khi xong.
    refetchInterval: (q) => (q.state.data && isKbBusy(q.state.data.entry.embedStatus) ? 3_000 : false),
  })
}

/** Sửa MỘT đoạn → chỉ đoạn đó được AI học lại (toàn văn dựng lại từ các đoạn). */
export function useUpdateKnowledgeChunk() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { entryId: string; chunkId: string; content: string }) => {
      const { data } = await api.patch<Envelope<{ chunk: KnowledgeChunk }>>(`/knowledge/${vars.entryId}/chunks/${vars.chunkId}`, { content: vars.content })
      return data.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['knowledge'] }),
  })
}
